import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Button } from '@/shared/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Loader2 } from 'lucide-react';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { TerminalType, UserTerminal } from '@/shared/types/userTerminalTypes';
import { useUpdateTerminalType } from '@/domains/terminals/services/user-terminals';
import { TERMINAL_TYPE_INFO } from '@/shared/utils/color-utils-terminals';

interface TerminalTypeDialogProps {
  open: boolean;
  terminal: UserTerminal;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const TerminalTypeDialog = ({
  open,
  onOpenChange,
  onSuccess,
  terminal,
}: TerminalTypeDialogProps) => {
  const [selectedType, setSelectedType] = useState<TerminalType | null>(
    terminal?.type ?? null
  );
  const updateTerminalType = useUpdateTerminalType();

  useEffect(() => {
    if (!open) {
      setSelectedType(terminal?.type ?? null);
    }
  }, [open, terminal?.type]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedType) {
      return;
    }

    try {
      await updateTerminalType.mutateAsync({
        terminalId: terminal.userTerminalId,
        type: selectedType,
      });
      showSuccess(SUCCESS.TERMINAL.UPDATED_TYPE);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.TERMINAL.UPDATE_TYPE);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Terminal Type</DialogTitle>
          <DialogDescription>
            Select the type for this terminal.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="terminal-type">Terminal Type</Label>
            <Select
              value={selectedType ?? undefined}
              onValueChange={(value) =>
                setSelectedType(value as TerminalType)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {Object.values(TerminalType).map((option) => (
                  <SelectItem key={option} value={option}>
                    {TERMINAL_TYPE_INFO[option].value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateTerminalType.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateTerminalType.isPending || !selectedType}>
              {updateTerminalType.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
